import { fetchSessionCapacity } from '../../api/ZionCapacityClient.ts'
import { isCapacitorNative } from '../../api/ZionServer.ts'
import { bootstrapRealtime } from '../../app/zionApp.ts'
import { navigateAfterAuth } from '../../app/navAfterAuth.ts'
import { Socket2Error } from '../../socket2/ZionSocketClient.ts'
import { importEncryptedBackup, BACKUP_FILE_ACCEPT } from '../../backup/ZionBackup.ts'
import {
  completeFirstLaunch,
  isSessionUnlocked,
  markSessionUnlocked,
  needsFirstLaunch,
  unlockWithMasterPassword,
} from '../../identity/ZionIdentityManager.ts'
import { isTotpEnabled, verifyActiveTotp } from '../../storage/totpStore.ts'
import { applyTheme, getCurrentTheme } from '../themes.ts'
import { splashLogoHtml } from '../../assets/appIcon.ts'
import { LEGAL_URLS, setLegalAccepted } from '../../storage/legalStore.ts'
import { isLanguageChosen, saveLocale, SUPPORTED_LOCALES } from '../../storage/languageStore.ts'
import { setActiveLocale, t, tf } from '../../i18n/index.ts'
import type { LocaleCode } from '../../i18n/types.ts'
import { appendBlocks, escapeHtml, setStatus } from '../dom.ts'
import { navigate } from '../router.ts'

type FirstLaunchStep = 2 | 3

let cancelAuthDeviceLink: (() => void) | null = null

function stopAuthDeviceLink(): void {
  cancelAuthDeviceLink?.()
  cancelAuthDeviceLink = null
}

async function onAuthDeviceLinkComplete(): Promise<void> {
  stopAuthDeviceLink()
  markSessionUnlocked()
  await bootstrapRealtime()
  navigate('home')
}

/**
 * Accoppiamento = opzionale. Offline-first: password sempre usabile.
 * Pulsante primario: "Salta e Crea Account".
 */
function enableAuthFormFields(root: HTMLElement): void {
  wireLegalCheckboxes(root)
  for (const sel of ['#password', '#password-confirm', '#btn-create', '#btn-unlock'] as const) {
    const el = root.querySelector(sel) as HTMLInputElement | HTMLButtonElement | null
    if (!el) continue
    el.disabled = false
    el.removeAttribute('readonly')
    el.style.pointerEvents = 'auto'
    el.style.opacity = '1'
  }
  const form = (root.querySelector('#setup-form') || root.querySelector('#unlock-form')) as HTMLElement | null
  if (form) {
    form.hidden = false
    form.style.display = ''
    form.style.pointerEvents = 'auto'
    form.style.position = 'relative'
    form.style.zIndex = '10'
  }
}

function wireOptionalDeviceLink(root: HTMLElement): void {
  const card = root.querySelector('#auth-optional-link') as HTMLElement | null
  const btn = root.querySelector('#btn-auth-device-link') as HTMLButtonElement | null
  const skipBtn = root.querySelector('#btn-auth-skip-link') as HTMLButtonElement | null
  const host = root.querySelector('#auth-device-link-host') as HTMLElement | null

  const focusPassword = (): void => {
    enableAuthFormFields(root)
    const pwd = root.querySelector('#password') as HTMLInputElement | null
    pwd?.focus()
    pwd?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }

  const dismissPairing = (silent = false): void => {
    stopAuthDeviceLink()
    if (host) {
      host.hidden = true
      host.innerHTML = ''
    }
    // Non nascondere tutta la card: resta il CTA skip + collega; scroll al form
    focusPassword()
    const pwdCard = root.querySelector('#auth-password-card') as HTMLElement | null
    pwdCard?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    if (!silent) {
      const status = root.querySelector('#auth-status') as HTMLElement | null
      if (status) {
        setStatus(status, 'Modalità senza accoppiamento — puoi creare o sbloccare con la password.', 'ok')
      }
    }
  }

  skipBtn?.addEventListener('click', () => dismissPairing(false))

  btn?.addEventListener('click', () => {
    void (async () => {
      if (!host || !card) return
      card.hidden = false
      btn.disabled = true
      host.hidden = false
      host.innerHTML = `<p class="status muted small">[INITIALIZING...]</p>`
      try {
        const {
          deviceLinkReceiverPanelHtml,
          mountDeviceLinkReceiverPanel,
        } = await import('../../deviceLink/deviceLinkReceiverPanel.ts')
        host.innerHTML = deviceLinkReceiverPanelHtml(true)
        stopAuthDeviceLink()
        const cancel = await mountDeviceLinkReceiverPanel(host, {
          onComplete: () => onAuthDeviceLinkComplete(),
          onSkip: () => dismissPairing(false),
        })
        cancelAuthDeviceLink = cancel
        enableAuthFormFields(root)
      } catch (err) {
        console.error('[ZION] DeviceSync failed — fallback offline (no block)', err)
        dismissPairing(false)
      } finally {
        btn.disabled = false
      }
    })()
  })

  enableAuthFormFields(root)

  // Capacitor / iPad: form password subito abilitato; QR solo su richiesta
  if (host) {
    host.hidden = true
    host.innerHTML = ''
  }
  stopAuthDeviceLink()
  enableAuthFormFields(root)
  // Porta subito al form (senza attendere rete)
  window.setTimeout(() => {
    const pwd = root.querySelector('#password') as HTMLInputElement | null
    const card = root.querySelector('#auth-password-card') as HTMLElement | null
    card?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    if (isCapacitorNative()) pwd?.focus()
  }, 0)
}

function optionalDeviceLinkHtml(): string {
  return `
    <section class="card premium-card auth-optional-link" id="auth-optional-link">
      <p class="status muted small">${escapeHtml(t('link_recv_hint_auth'))}</p>
      <button type="button" id="btn-auth-skip-link" class="big-cta">Salta e Crea Account</button>
      <button type="button" class="secondary" id="btn-auth-device-link">${escapeHtml(t('settings_btn_link_device'))}</button>
      <div id="auth-device-link-host" hidden></div>
    </section>
  `
}

/** Checkbox Privacy/Termini sempre reattive (indipendenti da rete/crypto). */
function wireLegalCheckboxes(root: HTMLElement): void {
  for (const id of ['chk-privacy', 'chk-terms'] as const) {
    const input = root.querySelector(`#${id}`) as HTMLInputElement | null
    if (!input) continue
    input.disabled = false
    input.removeAttribute('readonly')
    input.style.pointerEvents = 'auto'
    input.style.opacity = '1'
    const row = input.closest('.check-row') as HTMLElement | null
    if (row) {
      row.style.pointerEvents = 'auto'
      row.style.zIndex = '5'
    }
  }
}

function showPasswordFieldError(root: HTMLElement, message: string): void {
  const errEl = root.querySelector('#password-error') as HTMLElement | null
  const statusEl = root.querySelector('#auth-status') as HTMLElement | null
  if (errEl) {
    errEl.hidden = false
    errEl.textContent = message
  }
  if (statusEl) setStatus(statusEl, message, 'err')
}

function clearPasswordFieldError(root: HTMLElement): void {
  const errEl = root.querySelector('#password-error') as HTMLElement | null
  if (errEl) {
    errEl.hidden = true
    errEl.textContent = ''
  }
}

export async function renderAuthScreen(root: HTMLElement): Promise<void> {
  stopAuthDeviceLink()
  if (isSessionUnlocked() && !(await needsFirstLaunch())) {
    navigateAfterAuth()
    return
  }

  const firstLaunch = await needsFirstLaunch()
  if (firstLaunch && !isLanguageChosen()) {
    renderLanguageStep(root)
    return
  }
  if (firstLaunch) {
    await renderFirstLaunch(root, 2)
  } else {
    renderUnlock(root)
  }
}

function renderLanguageStep(root: HTMLElement): void {
  root.innerHTML = ''
  const options = SUPPORTED_LOCALES.map(
    (l) => `<option value="${l.code}">${l.label}</option>`,
  ).join('')
  appendBlocks(
    root,
    `
      <header class="splash compact">
        ${splashLogoHtml()}
        <p class="tagline">${t('lang_welcome_title')}</p>
      </header>
      <section class="card center premium-card">
        <p class="status muted">${t('lang_welcome_sub')}</p>
        <label for="lang-select">${t('settings_language_label')}</label>
        <select id="lang-select" class="lang-select">${options}</select>
        <button type="button" id="btn-lang-continue">${t('lang_continue')}</button>
      </section>
    `,
  )
  const select = root.querySelector('#lang-select') as HTMLSelectElement
  select.value = 'it'
  // Anteprima immediata della lingua scelta nel titolo/pulsanti
  const refreshPreview = () => {
    const code = select.value as LocaleCode
    setActiveLocale(code)
    const tagline = root.querySelector('.tagline')
    const sub = root.querySelector('.premium-card > .status')
    const label = root.querySelector('label[for="lang-select"]')
    const btn = root.querySelector('#btn-lang-continue')
    if (tagline) tagline.textContent = t('lang_welcome_title')
    if (sub) sub.textContent = t('lang_welcome_sub')
    if (label) label.textContent = t('settings_language_label')
    if (btn) btn.textContent = t('lang_continue')
  }
  select.addEventListener('change', refreshPreview)
  refreshPreview()

  root.querySelector('#btn-lang-continue')!.addEventListener('click', () => {
    const code = select.value as LocaleCode
    saveLocale(code)
    setActiveLocale(code)
    void renderAuthScreen(root)
  })
}

function renderUnlock(root: HTMLElement): void {
  root.innerHTML = ''
  appendBlocks(
    root,
    `
      <header class="splash compact">
        ${splashLogoHtml()}
        <p class="tagline">${t('auth_unlock_title')}</p>
      </header>
      ${optionalDeviceLinkHtml()}
      <section class="card premium-card" id="auth-password-card">
        <form id="unlock-form">
          <label for="password">${t('auth_password_master')}</label>
          <input id="password" type="password" autocomplete="current-password" required />
          <p id="password-error" class="status err" hidden></p>
          <button type="submit" id="btn-unlock">${t('auth_unlock_btn')}</button>
        </form>
        <p id="auth-status" class="status muted"></p>
      </section>
      ${renderRestoreBackupCard('restore-unlock')}
    `,
  )

  wireOptionalDeviceLink(root)
  wireRestoreBackup(root, 'restore-unlock')
  enableAuthFormFields(root)

  const statusEl = root.querySelector('#auth-status') as HTMLElement
  const submitBtn = root.querySelector('#btn-unlock') as HTMLButtonElement

  root.querySelector('#unlock-form')!.addEventListener('submit', async (e) => {
    e.preventDefault()
    clearPasswordFieldError(root)
    const password = (root.querySelector('#password') as HTMLInputElement).value
    submitBtn.disabled = true
    setStatus(statusEl, t('auth_verifying'))

    try {
      const ok = await unlockWithMasterPassword(password)
      if (!ok) {
        showPasswordFieldError(root, t('auth_wrong_password'))
        return
      }
      if (isTotpEnabled() && !isSessionUnlocked()) {
        renderTotpUnlockStep(root, password)
        return
      }
      await bootstrapRealtime()
      setStatus(statusEl, t('auth_unlocked'), 'ok')
      setTimeout(() => navigateAfterAuth(), 300)
    } catch (err) {
      console.error('[ZION] unlock failed', err)
      showPasswordFieldError(root, formatError(err))
    } finally {
      submitBtn.disabled = false
    }
  })
}

function renderTotpUnlockStep(root: HTMLElement, _passwordVerified: string): void {
  root.innerHTML = ''
  appendBlocks(
    root,
    `
      <header class="splash compact">
        ${splashLogoHtml()}
        <p class="tagline">${escapeHtml(t('auth_totp_title'))}</p>
      </header>
      <section class="card premium-card">
        <p class="status muted small">${escapeHtml(t('auth_totp_hint'))}</p>
        <form id="totp-unlock-form">
          <label for="totp-code">${escapeHtml(t('auth_totp_code_label'))}</label>
          <input id="totp-code" type="text" inputmode="numeric" autocomplete="one-time-code" maxlength="6" required placeholder="000000" />
          <button type="submit" id="btn-totp-unlock">${escapeHtml(t('auth_unlock_btn'))}</button>
        </form>
        <p id="auth-status" class="status muted"></p>
      </section>
    `,
  )
  const statusEl = root.querySelector('#auth-status') as HTMLElement
  const submitBtn = root.querySelector('#btn-totp-unlock') as HTMLButtonElement
  root.querySelector('#totp-unlock-form')!.addEventListener('submit', async (e) => {
    e.preventDefault()
    const code = (root.querySelector('#totp-code') as HTMLInputElement).value
    submitBtn.disabled = true
    setStatus(statusEl, t('auth_verifying'))
    try {
      const ok = await verifyActiveTotp(code)
      if (!ok) {
        setStatus(statusEl, t('auth_totp_wrong'), 'err')
        return
      }
      markSessionUnlocked()
      await bootstrapRealtime()
      setStatus(statusEl, t('auth_unlocked'), 'ok')
      setTimeout(() => navigateAfterAuth(), 300)
    } catch (err) {
      setStatus(statusEl, formatError(err), 'err')
    } finally {
      submitBtn.disabled = false
    }
  })
}

async function renderFirstLaunch(root: HTMLElement, step: FirstLaunchStep): Promise<void> {
  root.innerHTML = ''

  if (step === 2) {
    let cap: Awaited<ReturnType<typeof fetchSessionCapacity>> = null
    try {
      cap = await fetchSessionCapacity()
    } catch (err) {
      console.error('[ZION] capacity fetch ignored (Auth continua)', err)
    }
    const capLine = cap
      ? cap.open
        ? `<p class="status muted small center">${t('auth_beta_spots')} <strong>${cap.remaining}</strong> / ${cap.max}</p>`
        : `<p class="status err center">${tf('auth_beta_full', { max: cap.max })}</p>`
      : `<p class="status muted small center">${t('auth_beta_free')}</p>`

    const createDisabled = Boolean(cap && !cap.open)
    // Offline / capacity sconosciuta: non bloccare mai la creazione account
    const allowCreate = !createDisabled

    appendBlocks(
      root,
      `
        <header class="splash compact">
          ${splashLogoHtml()}
          <p class="tagline">${t('auth_access_title')}</p>
        </header>
        ${capLine}
        <section class="card premium-card" id="auth-password-card">
          <form id="setup-form" novalidate>
            <label for="password">${t('auth_password_min')}</label>
            <input id="password" type="password" autocomplete="new-password" required />
            <label for="password-confirm">${t('auth_password_confirm')}</label>
            <input id="password-confirm" type="password" autocomplete="new-password" required />
            <p id="password-error" class="status err" hidden role="alert"></p>
            <label class="check-row">
              <input id="chk-privacy" type="checkbox" required />
              <span>${t('auth_privacy_check')} <a href="${LEGAL_URLS.privacy}" target="_blank" rel="noopener">${t('auth_privacy_link')}</a></span>
            </label>
            <label class="check-row">
              <input id="chk-terms" type="checkbox" required />
              <span>${t('auth_terms_check')} <a href="${LEGAL_URLS.terms}" target="_blank" rel="noopener">${t('auth_terms_link')}</a></span>
            </label>
            <p class="status muted small">${t('auth_password_local_hint')}</p>
            <button type="submit" id="btn-create" ${allowCreate ? '' : 'disabled'}>${t('auth_create_identity')}</button>
          </form>
          <p id="auth-status" class="status muted"></p>
        </section>
        ${optionalDeviceLinkHtml()}
        ${renderRestoreBackupCard('restore-first')}
      `,
    )

    wireLegalCheckboxes(root)
    wireOptionalDeviceLink(root)
    enableAuthFormFields(root)
    // Form password sempre attivo — rete/socket non devono disabilitare i campi
    const createBtn = root.querySelector('#btn-create') as HTMLButtonElement | null
    if (createBtn && allowCreate) createBtn.disabled = false
    wireRestoreBackup(root, 'restore-first')

    const statusEl = root.querySelector('#auth-status') as HTMLElement
    const submitBtn = root.querySelector('#btn-create') as HTMLButtonElement

    root.querySelector('#setup-form')!.addEventListener('submit', async (e) => {
      e.preventDefault()
      clearPasswordFieldError(root)

      try {
        const password = (root.querySelector('#password') as HTMLInputElement).value
        const confirm = (root.querySelector('#password-confirm') as HTMLInputElement).value
        const privacy = (root.querySelector('#chk-privacy') as HTMLInputElement).checked
        const terms = (root.querySelector('#chk-terms') as HTMLInputElement).checked

        if (!privacy || !terms) {
          showPasswordFieldError(root, t('auth_accept_legal_err'))
          return
        }

        const { validateNewPassword } = await import('../../crypto/passwordValidation.ts')
        const check = validateNewPassword(password, confirm)
        if (!check.ok) {
          showPasswordFieldError(
            root,
            check.reason === 'mismatch' ? t('settings_pwd_err_mismatch') : t('settings_pwd_err_short'),
          )
          return
        }

        submitBtn.disabled = true
        setStatus(statusEl, t('auth_generating_keys'))

        // Resta sul form finché non riesce: così l'errore è sempre visibile.
        await setLegalAccepted(privacy, terms)
        const nickname = await completeFirstLaunch(password, confirm)

        await renderFirstLaunch(root, 3)
        const generatingEl = root.querySelector('#generating') as HTMLElement | null
        if (generatingEl) generatingEl.classList.add('hidden')
        const doneEl = root.querySelector('#done') as HTMLElement
        const nickEl = root.querySelector('#generated-nick') as HTMLElement
        if (nickEl) nickEl.textContent = nickname
        if (doneEl) doneEl.classList.remove('hidden')

        const statusDone = root.querySelector('#auth-status') as HTMLElement | null
        try {
          const { getSessionToken } = await import('../../storage/sessionStore.ts')
          if (!(await getSessionToken())?.trim() && statusDone) {
            setStatus(
              statusDone,
              'Identità locale creata. Rete non raggiungibile — tocca Entra e poi Riconnetti dalle Impostazioni.',
              'err',
            )
            const retry = document.createElement('button')
            retry.type = 'button'
            retry.className = 'secondary'
            retry.textContent = '🔄 Riconnetti'
            retry.addEventListener('click', () => {
              void (async () => {
                retry.disabled = true
                try {
                  const { createAnonymousSession } = await import('../../identity/ZionIdentityManager.ts')
                  await createAnonymousSession()
                  setStatus(statusDone, t('auth_unlocked'), 'ok')
                  retry.remove()
                } catch (reErr) {
                  setStatus(statusDone, formatError(reErr), 'err')
                } finally {
                  retry.disabled = false
                }
              })()
            })
            doneEl?.appendChild(retry)
          }
        } catch {
          /* ignore */
        }
      } catch (err) {
        console.error('[ZION] Crea identità fallita', err)
        const msg = formatError(err) || t('settings_pwd_err_generic')
        showPasswordFieldError(root, msg)
        submitBtn.disabled = false
      } finally {
        if (root.querySelector('#setup-form')) {
          submitBtn.disabled = false
        }
      }
    })
    return
  }

  appendBlocks(
    root,
    `
      <header class="splash compact">
        ${splashLogoHtml()}
      </header>
      <section class="card center premium-card">
        <div id="generating">
          <p class="status muted">${t('auth_generating_identity')}</p>
          <p class="status muted small">${t('auth_generating_detail')}</p>
        </div>
        <div id="done" class="hidden">
          <p class="status ok">${t('auth_you_are')} <strong id="generated-nick"></strong></p>
          <p class="status muted small">${t('auth_daily_suffix_hint')}</p>
          <button type="button" id="btn-enter">${t('auth_enter')}</button>
        </div>
        <p id="auth-status" class="status muted"></p>
      </section>
    `,
  )

  root.querySelector('#btn-enter')?.addEventListener('click', async () => {
    const statusEl = root.querySelector('#auth-status') as HTMLElement
    setStatus(statusEl, t('auth_starting'))
    try {
      await bootstrapRealtime()
      navigateAfterAuth()
    } catch (err) {
      setStatus(statusEl, formatError(err), 'err')
    }
  })
}

function formatError(err: unknown): string {
  if (err instanceof Socket2Error) {
    if (err.code === 'network_soft') {
      return 'Connessione in corso in background… puoi creare l’account con la password.'
    }
    if (
      err.code === 'session_create_failed' ||
      err.code === 'session_timeout' ||
      err.code === 'network_unreachable' ||
      /rete|timeout|network|failed|LOAD|cors/i.test(err.message)
    ) {
      return 'Rete non disponibile. Puoi comunque creare l’account con la password (Salta e Crea Account).'
    }
    return err.message
  }
  if (err instanceof DOMException) {
    const m = err.message || err.name
    if (/pattern|SyntaxError|abort|network/i.test(m) || err.name === 'AbortError') {
      return 'Connessione non disponibile. Puoi procedere offline con la password.'
    }
    return m
  }
  if (err instanceof Error) {
    if (/pattern|SyntaxError|device_sync|ephemeral_keys|LOAD|Failed|fetch|timeout|rete|sessione/i.test(err.message)) {
      return 'Rete/socket non raggiungibile. Usa «Salta e Crea Account» e crea la password localmente.'
    }
    return err.message
  }
  return String(err)
}

function renderRestoreBackupCard(prefix: string): string {
  return `
      <section class="card premium-card">
        <h2>${t('auth_restore_title')}</h2>
        <p class="status muted small">${t('auth_restore_hint')}</p>
        <label for="${prefix}-file">${t('auth_restore_file')}</label>
        <input id="${prefix}-file" type="file" accept="${BACKUP_FILE_ACCEPT}" />
        <p id="${prefix}-file-name" class="status muted small"></p>
        <label for="${prefix}-pass">${t('auth_restore_pass')}</label>
        <input id="${prefix}-pass" type="password" autocomplete="current-password" placeholder="${t('auth_restore_pass_ph')}" />
        <button type="button" class="secondary" id="${prefix}-btn">${t('auth_restore_btn')}</button>
        <p id="${prefix}-status" class="status muted"></p>
      </section>
    `
}

function wireRestoreBackup(root: HTMLElement, prefix: string): void {
  const statusEl = root.querySelector(`#${prefix}-status`) as HTMLElement
  const fileInput = root.querySelector(`#${prefix}-file`) as HTMLInputElement
  const fileNameEl = root.querySelector(`#${prefix}-file-name`) as HTMLElement
  let selectedFile: File | null = null

  fileInput.addEventListener('change', () => {
    selectedFile = fileInput.files?.[0] ?? null
    fileNameEl.textContent = selectedFile ? `${t('auth_file_prefix')} ${selectedFile.name}` : ''
  })

  root.querySelector(`#${prefix}-btn`)!.addEventListener('click', () => {
    void (async () => {
      const file = selectedFile ?? fileInput.files?.[0] ?? null
      const pass = (root.querySelector(`#${prefix}-pass`) as HTMLInputElement).value
      if (!file) {
        setStatus(statusEl, t('auth_restore_pick_file'), 'err')
        return
      }
      if (!pass.trim()) {
        setStatus(statusEl, t('auth_restore_need_pass'), 'err')
        return
      }
      setStatus(statusEl, t('auth_restoring'))
      try {
        const result = await importEncryptedBackup(file, pass, { allowUserMismatch: true })
        if (!result.ok) {
          setStatus(statusEl, result.message, 'err')
          return
        }
        selectedFile = null
        fileInput.value = ''
        fileNameEl.textContent = ''
        applyTheme(getCurrentTheme())
        markSessionUnlocked()
        await bootstrapRealtime()
        setStatus(statusEl, result.message, 'ok')
        setTimeout(() => navigateAfterAuth(), 600)
      } catch (err) {
        setStatus(statusEl, formatError(err), 'err')
      }
    })()
  })
}
